/**
 * Final standings computation (FR-1.6/4.6) — pure, over shared domain types.
 *
 * Lives in `shared` because both sides need it: the game-server emits standings
 * on `game_over`, and the web game-over screen derives the same ranking to render
 * co-winners. One definition, no drift (AGENT_GUIDE §5).
 *
 * Standard competition ranking: players sorted descending by score; tied players
 * share a rank (and the next rank skips accordingly). `winnerIds` is the set of
 * rank-1 players — one entry normally, multiple on a tie for first (co-winners).
 */

import type { Player, Standing } from './domain';

export interface StandingsResult {
  standings: Standing[];
  /** All rank-1 players (co-winners on a tie for first). */
  winnerIds: string[];
}

/** Compute ranked standings + the rank-1 winner set from player scores. */
export function computeStandings(players: Player[]): StandingsResult {
  const sorted = [...players].sort((a, b) => b.score - a.score);
  const standings: Standing[] = [];
  let rank = 0;
  let prevScore: number | null = null;
  sorted.forEach((player, index) => {
    if (prevScore === null || player.score !== prevScore) {
      rank = index + 1; // competition ranking (ties share, then skip)
      prevScore = player.score;
    }
    standings.push({
      playerId: player.id,
      name: player.name,
      score: player.score,
      rank,
      ...(player.avatarUrl ? { avatarUrl: player.avatarUrl } : {}),
    });
  });
  const winnerIds = standings.filter((s) => s.rank === 1).map((s) => s.playerId);
  return { standings, winnerIds };
}

/**
 * The sole winner id, or `null` when more than one player ties for first
 * (co-winners) or there are no players. This is the wire `game_over.winnerId`.
 */
export function soleWinnerId(result: StandingsResult): string | null {
  return result.winnerIds.length === 1 ? result.winnerIds[0]! : null;
}
